import { Subject } from "rxjs";
import { parseColumnUi, type ColumnUi } from "../../services/text-viewer-service";
import { GridColDef, GridColumnVisibilityModel } from "@mui/x-data-grid";
import { GridApiCommunity } from "@mui/x-data-grid/internals";
import { MappedIcons } from "../icon-factory/icon-factory";
import GlobalClient, {
  CdcTable,
  ChangeDataCaptureEvent,
} from "../../hermesclient/client";
import {
  BiqlQuery,
  CalculatedField,
  BiqlField,
  QubesAPI,
} from "../../qubes-client/api";
import { ContentType, RpcFrameType } from "../../hermesclient/proto/wsmessages";
import { nameMappingService } from "../../services/NameMappingService";
import { asLogicalMailbox } from "../../hermesclient/types";
import { Duration } from "../../duration";
import { createCdcRefetchCoalescer, type CdcRefetchCoalescer } from "./cdc-coalescer";
import { UnavailableCell } from "./query-error";
import { ViewDependency } from "../../model/qubes/qubes-model";
import { FieldDef } from "../../model/qubes/field";

export type RowIdT = string;

export type RowUpsert<RowT> = {
  type: "upsert";
  row: RowT;
};

export type RowDelete = {
  type: "delete";
  rowId: RowIdT;
};

export type ShowModalFn = (json: object) => void;

export interface ActionCtx<RowT> {
  tableModel: TableModel<RowT>;
  api: GridApiCommunity;
  row: RowT;
  showModalFn: ShowModalFn;
  rowIndex?: number;
  colIndex?: number;
}

export type RowAction<RowT> = {
  name: string;
  icon: MappedIcons;
  /** when false for a row, the action shows disabled on that row */
  when?: (row: RowT) => boolean;
  /**
   * A submenu: this entry opens these instead of running. Used by the page
   * DSL's borrowed action groups — a schedule row's "Job" entry holds the
   * whole Jobs-page menu
   * (FEATURE-20260918-unified-job-menu-on-jobs-schedules-monitors).
   */
  children?: RowAction<RowT>[];
  apply?: (
    tableModel: TableModel<RowT>,
    api: GridApiCommunity,
    row: RowT,
    rowIndex?: number,
    colIndex?: number,
  ) => void;
  run?: (ctx: ActionCtx<RowT>) => Promise<void> | void;
};

export type RowChange<RowT> = RowUpsert<RowT> | RowDelete;

export class ResolvedTableModel {
  readonly tableModel: TableModel<Record<string, unknown>>;
  readonly columnVisibilityModel: GridColumnVisibilityModel;

  constructor(tableModel: TableModel<Record<string, unknown>>) {
    this.tableModel = tableModel;
    this.columnVisibilityModel = {};
    tableModel.columns.forEach((columnDef) => {
      if (columnDef.muiColDef.field) {
        const visible = !(columnDef.hidden ?? false);
        this.columnVisibilityModel[columnDef.muiColDef.field] = visible;
      }
    });
  }
}

export interface TableModel<RowT> {
  columns: ColumnDef<RowT>[];
  initialRows: RowT[];
  rowId(row: RowT): RowIdT;
  realtime?: Subject<RowChange<RowT>>;
  actions?: RowAction<RowT>[];
  currentRows: Map<RowIdT, RowT>;
  cleanup?: () => void;
  refreshState?: Subject<RefreshState>;
  /** programmatic refresh to re-run the underlying query */
  refresh?: (filters?: BiqlFilter[]) => Promise<void>;
}

export interface RefreshState {
  isRefreshing: boolean;
  /** when the last SUCCESSFUL fetch completed; a failed fetch leaves it unchanged */
  lastRefreshedAt?: number;
  /** the last fetch failed with this message (cleared by the next success) */
  error?: string;
  /** the query the failed fetch sent, when the model has one */
  errorQuery?: string;
  /** the model refetches on this period, so a failure is retried */
  retryEveryMs?: number;
  /**
   * The view's query failed on a column the live model lacks, and it now runs
   * without its optional columns: they render blank, and the grid says so.
   * Set for the rest of the model's life (one retry per grid load).
   */
  degraded?: { columns: string[]; labels: string[]; reason: string };
}

/**
 * The same SELECT without the view's optional columns: what the model runs when
 * the live firewall model lacks one of them (a UI newer than its server).
 */
export interface OptionalColumnsFallback {
  sql: string;
  /** the columns the fallback leaves out (Copy details names them) */
  columns: string[];
  /** the shown columns that go blank, as the grid labels them (the warning names them) */
  labels: string[];
}

function isColumnNotFound(err: unknown): boolean {
  return /column not found/i.test(err instanceof Error ? err.message : String(err));
}

export interface ColumnDef<RowT> {
  muiColDef: GridColDef;
  hidden?: boolean;
  fieldDef: FieldDef;
  valueGetter?: (row: RowT) => unknown;
  renderCell?: (row: RowT) => React.ReactNode;
}

export function resolveColumnDef<RowT>(
  tableModel: TableModel<RowT>,
  columnDef: ColumnDef<RowT>,
): GridColDef {
  const gcd = { ...columnDef.muiColDef };
  const valueGetterFn = columnDef.valueGetter;
  if (valueGetterFn) {
    gcd.valueGetter = (_value, gridRow) => {
      const row = tableModel.currentRows.get(String(gridRow.id));
      if (row) {
        return valueGetterFn(row);
      }
    };
  }
  const renderCellFn = columnDef.renderCell;
  if (renderCellFn) {
    gcd.renderCell = (params) => {
      const row = tableModel.currentRows.get(params.id as string);
      if (row) {
        return renderCellFn(row);
      }
    };
  }
  gcd.field = columnDef.muiColDef.field;
  return gcd;
}

function createColumnDefFromCalculatedField(
  field: CalculatedField,
): ColumnDef<Record<string, unknown>> | undefined {
  const fieldName = field.name;
  if (fieldName) {
    const label = field.label;
    const muiColumnDef: GridColDef = {
      field: fieldName,
      headerName: label,
      flex: 1,
      align: field.align,
      headerAlign: field.align,
    };
    return {
      hidden: false,
      muiColDef: muiColumnDef,
      fieldDef: {} as FieldDef,
      valueGetter: (row) => field.getValue(row, field),
      renderCell: (row) => field.getDisplayComponent(row, field),
    } satisfies ColumnDef<Record<string, unknown>>;
  }
}

function createColumnDef(field: BiqlField): ColumnDef<Record<string, unknown>> | undefined {
  const fieldName = field.name;
  if (fieldName) {
    const label = field.label;
    const muiColumnDef: GridColDef = {
      field: fieldName,
      headerName: label ?? fieldName,
      flex: 1,
    };
    return {
      hidden: field.hidden ?? false,
      muiColDef: muiColumnDef,
      fieldDef: field,
      valueGetter: (row) => field.getValue(row, field),
      renderCell: (row) => field.getDisplayComponent(row, field),
    };
  }
}

export interface BiqlTableModelArgs {
  biqlQuery: BiqlQuery;
  actions: RowAction<Record<string, unknown>>[];
  fields?: FieldDef[];
}

/**
 * Compare two sets of rows and detect changes
 * @param oldRows Current rows in the table
 * @param newRows Freshly fetched rows
 * @param getPrimaryKey Function to extract primary key from row
 * @returns Object containing arrays of rows to upsert and IDs to delete
 */
function diffRows<T>(
  oldRows: T[],
  newRows: T[],
  getPrimaryKey: (row: T) => string,
): {
  upserts: T[];
  deletes: string[];
} {
  const oldRowsById = new Map<string, T>();
  oldRows.forEach((row) => {
    oldRowsById.set(getPrimaryKey(row), row);
  });

  const newRowsById = new Map<string, T>();
  const upserts: T[] = [];

  // Check for new or changed rows
  newRows.forEach((newRow) => {
    const id = getPrimaryKey(newRow);
    newRowsById.set(id, newRow);

    const oldRow = oldRowsById.get(id);
    if (!oldRow) {
      // New row
      upserts.push(newRow);
    } else {
      // Check if changed (deep equality)
      const changed = JSON.stringify(oldRow) !== JSON.stringify(newRow);
      if (changed) {
        upserts.push(newRow);
      }
    }
  });

  // Check for deleted rows
  const deletes: string[] = [];
  oldRows.forEach((oldRow) => {
    const id = getPrimaryKey(oldRow);
    if (!newRowsById.has(id)) {
      deletes.push(id);
    }
  });

  return { upserts, deletes };
}

export interface ChangeDataCaptureArgs<RowIdT> {
  database: string;
  table: string;
  extraMatchers?: ChangeDataCaptureMatcher<Record<string, unknown>, RowIdT>[];
}

export interface ChangeDataCaptureMatcher<MatcherRowT, RowIdT> {
  table: CdcTable;
  listener: (
    tableModel: TableModel<Record<string, unknown>>,
    cdcEvent: ChangeDataCaptureEvent,
    row: MatcherRowT,
  ) => Array<RowIdT>;
}

/**
 * The one writer of a model's RefreshState. The last success time and the last
 * failure persist across fetches, so a refresh in flight never clears an error
 * the grid is showing, and a failed fetch never moves "last refreshed".
 */
export function trackRefreshState(refreshState: Subject<RefreshState>, retryEveryMs?: number) {
  let lastRefreshedAt: number | undefined;
  let error: string | undefined;
  let errorQuery: string | undefined;
  let degraded: RefreshState["degraded"];
  const emit = (isRefreshing: boolean) =>
    refreshState.next({ isRefreshing, lastRefreshedAt, error, errorQuery, retryEveryMs, degraded });
  return {
    started: () => emit(true),
    degraded: (fallback: OptionalColumnsFallback, reason: string) => {
      degraded = { columns: fallback.columns, labels: fallback.labels, reason };
    },
    succeeded: () => {
      lastRefreshedAt = Date.now();
      error = undefined;
      errorQuery = undefined;
      emit(false);
    },
    failed: (err: unknown, query?: string) => {
      error = err instanceof Error ? err.message : String(err);
      errorQuery = query;
      emit(false);
    },
  };
}

export function createTableModel(args: BiqlTableModelArgs): TableModel<Record<string, unknown>> {
  const subject = new Subject<RowChange<Record<string, unknown>>>();
  const refreshState = new Subject<RefreshState>();
  const emitRefreshState = trackRefreshState(
    refreshState,
    args.biqlQuery.getRefreshInterval()?.toMilliseconds(),
  );

  // create a reusable fetch function that can be called initially, periodically, or on-demand
  const fetchAll = async (filters?: BiqlFilter[]) => {
    const clonedQuery = args.biqlQuery.clone();
    filters?.forEach((filter) => {
      clonedQuery.appendWhere(filter.toWhereClause());
    });
    const query = clonedQuery.buildQueryString();
    emitRefreshState.started();
    try {
      const response = await QubesAPI.get().biqlQuery<Record<string, unknown>>(
        query,
        clonedQuery.appSpace(),
        "verbose",
      );

      const oldRows = Array.from(tableModel.currentRows.values());
      const newRows = response.data;
      const { upserts, deletes } = diffRows(oldRows, newRows, tableModel.rowId);

      upserts.forEach((row) => subject.next({ type: "upsert", row }));
      deletes.forEach((rowId) => subject.next({ type: "delete", rowId }));
      emitRefreshState.succeeded();
    } catch (err) {
      console.error("Error fetching table rows:", err);
      emitRefreshState.failed(err, query);
    }
  };

  const columns0 = new Array<ColumnDef<Record<string, unknown>>>();
  if (args.fields && args.fields.length > 0) {
    args.fields.forEach((field) => {
      const cd = {
        hidden: field.hidden ?? false,
        muiColDef: {
          field: field.name!,
          headerName: field.label ?? field.name,
          flex: 1,
        },
        fieldDef: field,
        valueGetter: (row: Record<string, unknown>) => field.getValue(row, field),
        renderCell: (row: Record<string, unknown>) => field.getDisplayComponent(row, field),
      } satisfies ColumnDef<Record<string, unknown>>;
      columns0.push(cd);
    });
  } else {
    args.biqlQuery.fields().forEach((field) => {
      const cd = createColumnDef(field);
      if (cd) {
        columns0.push(cd);
      }
    });
  }

  args.biqlQuery.calculatedFields().forEach((field) => {
    const cd = createColumnDefFromCalculatedField(field);
    if (cd) {
      columns0.push(cd);
    }
  });

  const tableModel: TableModel<Record<string, unknown>> = {
    columns: columns0,
    initialRows: [],
    rowId: (row) => row["uid"] as string,
    realtime: subject,
    actions: args.actions,
    currentRows: new Map<RowIdT, Record<string, unknown>>(),
    refresh: async (filters?: BiqlFilter[]) => {
      await fetchAll(filters);
    },
  };

  const cdc = args.biqlQuery.changeDataCaptureArgs();
  // One refetch per CDC window, not per event — see cdc-coalescer.ts.
  // BUG-20260913-cdc-refetch-per-event-hammers-dbaccess.
  let cdcCoalescer: CdcRefetchCoalescer | undefined;
  if (cdc) {
    const fetchRows = async function (
      uids: Array<string>,
    ): Promise<Record<string, unknown>[]> {
      const response = await QubesAPI.get().biqlQuery(
        args.biqlQuery
          .clone()
          .appendWhere(
            "uid in (" + uids.map((uid) => '"' + uid + '"').join(",") + ")",
          )
          .buildQueryString(),
        args.biqlQuery.appSpace(),
        "verbose",
      );
      return response.data as Record<string, unknown>[];
    };

    const matchers: Array<CdcTable> = [cdc.table];

    const cdcListenersByTable = new Map<
      string,
      ChangeDataCaptureMatcher<Record<string, unknown>, string>
    >();

    args.biqlQuery.fields().forEach((field) => {
      if (field.foreignCdc && field.name) {
        const fieldNameC = field.name;
        const fcdc = field.foreignCdc;
        const table = {
          database: cdc.table.database,
          table: fcdc.table,
        };
        matchers.push(table);

        const listener = function (
          tableModel: TableModel<Record<string, unknown>>,
          cdcEvent: ChangeDataCaptureEvent,
          row: Record<string, unknown>,
        ): Array<string> {
          const foreignRowId = row[fcdc.pk];
          const rowIds = new Array<string>();
          tableModel.currentRows.forEach((v, _k) => {
            if (v[fieldNameC] === foreignRowId) {
              rowIds.push(tableModel.rowId(v));
            }
          });
          return rowIds;
        };

        cdcListenersByTable.set(fcdc.table, {
          table: table,
          listener: listener,
        });
      }
    });

    const refetchRows = async (rowIds: string[]) => {
      const resolvedRows = await fetchRows(rowIds);
      if (resolvedRows) {
        resolvedRows.forEach((row) => {
          tableModel.realtime?.next({ type: "upsert", row: row });
        });
        const found = new Set(resolvedRows.map((row) => tableModel.rowId(row)));
        rowIds.forEach((rowId) => {
          if (!found.has(rowId)) {
            tableModel.realtime?.next({ type: "delete", rowId: rowId });
          }
        });
      }
    };
    cdcCoalescer = createCdcRefetchCoalescer(refetchRows);

    const cdcListener = function (
      changeEvent: ChangeDataCaptureEvent,
      row: Record<string, unknown>,
    ) {
      console.log("ChangeDataCaptureEvent", changeEvent);
      let rowIds: Array<string> = [];
      if (changeEvent.table === cdc.table.table) {
        const rowId: string = row["uid"] as string;
        rowIds.push(rowId);
      } else {
        const em = cdcListenersByTable.get(changeEvent.table);
        const rowIds0 = em?.listener(tableModel, changeEvent, row);
        if (rowIds0) {
          rowIds = rowIds0;
        }
      }
      cdcCoalescer?.add(rowIds);
    };

    GlobalClient.get().cdcSubscribe<Record<string, unknown>>(
      {
        tables: matchers,
        startSeq: "new",
      },
      cdcListener,
    );
  }

  // Set up periodic refresh if refreshInterval is configured
  const refreshInterval = args.biqlQuery.getRefreshInterval();
  tableModel.refreshState = refreshState;
  if (refreshInterval) {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let lastCdcUpdateTime = 0;
    const cdcUpdateThrottle = Duration.fromSeconds(2);

    const performRefresh = async () => {
      // Skip refresh if CDC updated recently (within last 2 seconds)
      const now = Date.now();
      if (now - lastCdcUpdateTime < cdcUpdateThrottle.toMilliseconds()) {
        scheduleNextRefresh();
        return;
      }

      // fetchAll publishes the refresh state, including a failure.
      try {
        await fetchAll();
      } catch (error) {
        console.error("Error during periodic refresh:", error);
      }

      scheduleNextRefresh();
    };

    const scheduleNextRefresh = () => {
      timeoutId = setTimeout(performRefresh, refreshInterval.toMilliseconds());
    };

    // Track CDC updates to avoid redundant refreshes
    if (cdc) {
      const originalNext = subject.next.bind(subject);
      subject.next = (value: RowChange<Record<string, unknown>>) => {
        lastCdcUpdateTime = Date.now();
        originalNext(value);
      };
    }

    // Start the refresh cycle
    scheduleNextRefresh();

    // Cleanup function
    tableModel.cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      refreshState.complete();
    };
  }

  if (cdcCoalescer) {
    const coalescer = cdcCoalescer;
    const previousCleanup = tableModel.cleanup;
    tableModel.cleanup = () => {
      coalescer.cancel();
      previousCleanup?.();
    };
  }

  return tableModel;
}

export interface SqlFirewallTableModelArgs {
  sql?: string;
  /** run instead of sql once sql fails on a column the live model lacks */
  optionalFallback?: OptionalColumnsFallback;
  /** instead of sql — see SqlFirewallView.fetch */
  fetch?: (where: string[]) => Promise<Record<string, unknown>[]>;
  /** row identity: these row keys joined with ":" (default: uid) — see SqlFirewallView.rowKey */
  rowKey?: string[];
  actions: RowAction<Record<string, unknown>>[];
  fields: FieldDef[];
  calculatedFields?: CalculatedField[];
  cdc?: { database: string; table: string };
  refreshInterval?: Duration;
  /**
   * Drill-down gate: the view's dependency filters. When ANY of these is
   * present and unsatisfied, the model fetches NOTHING — no initial query, no
   * CDC upserts, no interval refresh — so a child view stays empty until its
   * parent is selected (instead of loading its whole table on mount and then
   * getting narrowed). Once satisfied, QubesView drives refresh(filters).
   * Absent/empty = always fetch (a top-level view like Schemas).
   */
  gateFilters?: BiqlFilter[];
}

export interface SqlFirewallColumnMeta {
  name: string;
  sqlType: string;
  nullable: boolean;
  dataTypeSize: number;
  typeModifier: number;
  /** the table (the firewall's logical name) and column the value came from; empty for an expression */
  sourceTable: string;
  sourceColumn: string;
  /** the model's UI metadata for the source column, when it has any */
  ui?: ColumnUi;
}

export interface SqlFirewallResult {
  rows: Record<string, unknown>[];
  transformedQuery?: string;
  columns?: SqlFirewallColumnMeta[];
}

export interface SqlFirewallQueryOptions {
  runAsUser?: string;
  noTransform?: boolean;
  /** A database_connection name (e.g. a Trino); absent = the continuum database. */
  database?: string;
}

export async function sqlFirewallQueryFull(
  sql: string,
  opts?: SqlFirewallQueryOptions,
): Promise<SqlFirewallResult> {
  console.log("[sql-firewall] query:", sql, opts ?? "");
  const dbMailbox = await nameMappingService.resolve(asLogicalMailbox("db"));
  const client = GlobalClient.get();
  const body: Record<string, unknown> = { sql };
  if (opts?.runAsUser) body.runAsUser = opts.runAsUser;
  if (opts?.noTransform) body.noTransform = true;
  if (opts?.database) body.database = opts.database;
  const requestBytes = new TextEncoder().encode(JSON.stringify(body));
  const rpcResponse = await client.rawRpcCall({
    to: dbMailbox,
    endPoint: "dbaccess.v1.Query",
    body: requestBytes,
    contentType: ContentType.Json,
  });
  const frameType = rpcResponse.header?.rpcHeader?.frameType;
  if (frameType === RpcFrameType.ErrorResponse) {
    const errorInfo = rpcResponse.header?.rpcHeader?.errorInfo;
    const msg =
      errorInfo?.message ||
      errorInfo?.errorNode?.frame?.message ||
      "dbaccess.v1.Query failed";
    console.error("[sql-firewall] query failed:", sql, "\n  error:", msg);
    throw new Error(msg);
  }
  if (!rpcResponse.data || rpcResponse.data.length === 0) {
    console.log("[sql-firewall] result: empty response");
    return { rows: [] };
  }
  const raw = JSON.parse(new TextDecoder().decode(rpcResponse.data));
  const rows = raw.rows ?? [];
  const transformedQuery = raw.transformedSql ?? raw.transformed_sql;
  const columns: SqlFirewallColumnMeta[] | undefined = raw.columns?.map(
    (c: {
      name?: string;
      sqlType?: string;
      sql_type?: string;
      nullable?: boolean;
      dataTypeSize?: number;
      data_type_size?: number;
      typeModifier?: number;
      type_modifier?: number;
      sourceTable?: string;
      source_table?: string;
      sourceColumn?: string;
      source_column?: string;
      metadata?: string;
    }) => ({
      name: c.name ?? "",
      sqlType: c.sqlType ?? c.sql_type ?? "unknown",
      nullable: c.nullable ?? true,
      dataTypeSize: c.dataTypeSize ?? c.data_type_size ?? 0,
      typeModifier: c.typeModifier ?? c.type_modifier ?? 0,
      sourceTable: c.sourceTable ?? c.source_table ?? "",
      sourceColumn: c.sourceColumn ?? c.source_column ?? "",
      ui: parseColumnUi(c.metadata),
    }),
  );
  console.log(
    "[sql-firewall] transformed:",
    transformedQuery ?? "(not returned)",
  );
  console.log("[sql-firewall] columns:", columns);
  console.log("[sql-firewall] result:", rows.length, "rows", rows);
  return { rows, transformedQuery, columns };
}

export async function sqlFirewallQuery(sql: string): Promise<Record<string, unknown>[]> {
  const result = await sqlFirewallQueryFull(sql);
  return result.rows;
}

export interface PrettyPrintOptions {
  spaces?: number;
  keyword_case?: number; // CaseFolding enum: 0=default, 1=unchanged, 2=lowercase, 3=uppercase, 4=capitalize
  comma_placement?: number; // CommaPlacement enum: 0=default, 1=end (trailing), 2=start (leading)
  no_extra_line?: boolean;
}

export async function prettyPrintSql(
  sql: string,
  options?: PrettyPrintOptions,
): Promise<string> {
  const dbMailbox = await nameMappingService.resolve(asLogicalMailbox("db"));
  const client = GlobalClient.get();
  const reqBody: Record<string, unknown> = { sql };
  if (options?.spaces !== undefined) reqBody.spaces = options.spaces;
  if (options?.keyword_case !== undefined)
    reqBody.keyword_case = options.keyword_case;
  if (options?.comma_placement !== undefined)
    reqBody.comma_placement = options.comma_placement;
  if (options?.no_extra_line !== undefined)
    reqBody.no_extra_line = options.no_extra_line;
  const reqBytes = new TextEncoder().encode(JSON.stringify(reqBody));
  const resp = await client.rawRpcCall({
    to: dbMailbox,
    endPoint: "dbaccess.v1.PrettyPrint",
    body: reqBytes,
    contentType: ContentType.Json,
  });
  const ft = resp.header?.rpcHeader?.frameType;
  if (ft === RpcFrameType.ErrorResponse) {
    const errorInfo = resp.header?.rpcHeader?.errorInfo;
    throw new Error(errorInfo?.message || "PrettyPrint failed");
  }
  const parsed = JSON.parse(new TextDecoder().decode(resp.data));
  // PrettyPrintRpc returns a success frame even when pg_format errored;
  // the failure is conveyed by a non-empty `error` field in the body.
  // Surface that here so callers don't see a silent no-op when, e.g.,
  // pg_format isn't installed on the db host.
  const err = parsed.error || parsed.Error;
  if (err) {
    throw new Error(`PrettyPrint: ${err}`);
  }
  return parsed.formattedSql || parsed.formatted_sql || sql;
}

export function appendSqlWhere(baseSql: string, conditions: string[]): string {
  if (conditions.length === 0) return baseSql;
  const trimmed = baseSql.trimEnd().replace(/;$/, "");
  const whereClause = conditions.join(" AND ");

  // The condition must go into the WHERE, which is BEFORE any trailing
  // ORDER BY / GROUP BY / LIMIT. A naive append to the end would produce
  // `... ORDER BY x AND cond` — invalid SQL — so split off any trailing
  // clause, inject into the WHERE, then re-append the tail.
  const tailMatch = trimmed.match(/\s+\b(ORDER\s+BY|GROUP\s+BY|LIMIT|OFFSET)\b/i);
  const head = tailMatch ? trimmed.slice(0, tailMatch.index) : trimmed;
  const tail = tailMatch ? trimmed.slice(tailMatch.index) : "";

  const merged = /\bWHERE\b/i.test(head)
    ? `${head} AND ${whereClause}`
    : `${head} WHERE ${whereClause}`;
  return `${merged}${tail}`;
}

export function createSqlFirewallTableModel(
  args: SqlFirewallTableModelArgs,
): TableModel<Record<string, unknown>> {
  const subject = new Subject<RowChange<Record<string, unknown>>>();

  // Build lowercase → original field name mapping for servers that lowercase column aliases
  const fieldKeyMap = new Map<string, string>();
  for (const f of args.fields) {
    if (f.name) fieldKeyMap.set(f.name.toLowerCase(), f.name);
  }
  const remapRowKeys = (row: Record<string, unknown>): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(row)) {
      out[fieldKeyMap.get(key.toLowerCase()) ?? key] = row[key];
    }
    return out;
  };

  // Loading-state Subject so the grid can show a spinner/overlay while a query
  // runs (e.g. when selecting a schedule loads its events). Emitted by every
  // fetchAll(), so selection-triggered fetches get feedback — not just the
  // refreshInterval path.
  const refreshState = new Subject<RefreshState>();
  const emitRefreshState = trackRefreshState(refreshState, args.refreshInterval?.toMilliseconds());

  // Drill-down gate: a child view with an unsatisfied dependency filter must
  // fetch nothing (stay empty until its parent is selected). Top-level views
  // (no gateFilters) always pass.
  const gateOpen = (): boolean =>
    (args.gateFilters ?? []).every((f) => f.isSatisfied());

  // Once the query has failed on a missing column and the fallback worked, the
  // fallback is the view's query for the rest of this model's life.
  let useFallback = false;
  let fallbackReason = "";
  const baseSql = (): string => (useFallback && args.optionalFallback ? args.optionalFallback.sql : args.sql ?? "");
  const runSql = async (sql: string) => (await sqlFirewallQuery(sql)).map(remapRowKeys);

  const fetchAll = async (filters?: BiqlFilter[]) => {
    if (!gateOpen()) {
      // Ungated: ensure the grid is empty and skip the query entirely.
      const oldIds = Array.from(tableModel.currentRows.keys());
      oldIds.forEach((rowId) => {
        tableModel.currentRows.delete(rowId);
        subject.next({ type: "delete", rowId });
      });
      return;
    }
    const conditions: string[] = [];
    filters?.forEach((filter) => {
      if (filter.isSatisfied()) {
        conditions.push(filter.toWhereClause());
      }
    });
    emitRefreshState.started();
    let query = args.fetch ? undefined : appendSqlWhere(baseSql(), conditions);
    try {
      let rows: Record<string, unknown>[];
      if (query === undefined) {
        rows = (await args.fetch!(conditions)).map(remapRowKeys);
      } else {
        try {
          rows = await runSql(query);
        } catch (err) {
          const fallback = args.optionalFallback;
          if (!fallback || useFallback || !isColumnNotFound(err)) throw err;
          // A required column missing fails the fallback too, and that error is shown.
          const reason = err instanceof Error ? err.message : String(err);
          console.warn(
            `[sql-firewall] the live model lacks a column this view asks for (${reason}); ` +
              `retrying without its optional columns: ${fallback.columns.join(", ")}`,
          );
          useFallback = true;
          fallbackReason = reason;
          emitRefreshState.degraded(fallback, reason);
          query = appendSqlWhere(fallback.sql, conditions);
          rows = await runSql(query);
        }
      }

      // This query result is the COMPLETE, authoritative answer for the current
      // filter (e.g. all events for the selected schedule). Diff against the
      // currently-shown rows and, crucially, keep currentRows in sync HERE rather
      // than relying on the async grid subscription. Without that, two selections
      // in quick succession (or a CDC tick) race on a stale currentRows baseline,
      // so re-selecting a schedule could show the wrong count (2.8K one time, 1
      // the next). fetchAll owns the row set; the subscription only mirrors it.
      const newIds = new Set(rows.map((r) => tableModel.rowId(r)));
      const oldRows = Array.from(tableModel.currentRows.values());
      const { upserts } = diffRows(oldRows, rows, tableModel.rowId);
      const deletes = oldRows
        .map((r) => tableModel.rowId(r))
        .filter((id) => !newIds.has(id));

      deletes.forEach((rowId) => {
        tableModel.currentRows.delete(rowId);
        subject.next({ type: "delete", rowId });
      });
      upserts.forEach((row) => {
        tableModel.currentRows.set(tableModel.rowId(row), row);
        subject.next({ type: "upsert", row });
      });
      emitRefreshState.succeeded();
    } catch (err) {
      console.error("Error fetching sql-firewall table rows:", err);
      emitRefreshState.failed(err, query);
    }
  };

  const columns0: ColumnDef<Record<string, unknown>>[] = args.fields.map((field) => ({
    hidden: field.hidden ?? false,
    muiColDef: {
      field: field.name!,
      headerName: field.label ?? field.name,
      flex: 1,
    },
    fieldDef: field,
    valueGetter: (row: Record<string, unknown>) => field.getValue(row, field),
    renderCell: (row: Record<string, unknown>) => field.getDisplayComponent(row, field),
  }));

  args.calculatedFields?.forEach((field) => {
    const cd = createColumnDefFromCalculatedField(field);
    if (cd) columns0.push(cd);
  });

  // Once the view runs without its optional columns, the shown columns built
  // from them say "unavailable" rather than the "—" of a row that has no value.
  const fallbackLabels = new Set(args.optionalFallback?.labels ?? []);
  for (const col of columns0) {
    if (!fallbackLabels.has(String(col.muiColDef.headerName ?? col.muiColDef.field))) continue;
    const renderCell = col.renderCell;
    const valueGetter = col.valueGetter;
    col.renderCell = (row) => (useFallback ? <UnavailableCell reason={fallbackReason} /> : renderCell?.(row));
    col.valueGetter = (row) => (useFallback ? "" : valueGetter ? valueGetter(row) : row[col.muiColDef.field]);
  }

  const tableModel: TableModel<Record<string, unknown>> = {
    columns: columns0,
    initialRows: [],
    // The grid's row identity: `uid`, or the named columns joined (a monitor
    // with two jobs is two rows: monitor uid + job uid).
    rowId: (row) =>
      args.rowKey?.length
        ? args.rowKey.map((k) => String(row[k] ?? "")).join(":")
        : (row["uid"] as string),
    realtime: subject,
    actions: args.actions,
    currentRows: new Map<RowIdT, Record<string, unknown>>(),
    refreshState,
    refresh: async (filters?: BiqlFilter[]) => {
      await fetchAll(filters);
    },
  };

  // One refetch per CDC window, not per event — see cdc-coalescer.ts.
  // BUG-20260913-cdc-refetch-per-event-hammers-dbaccess.
  let cdcCoalescer: CdcRefetchCoalescer | undefined;
  if (args.cdc) {
    const cdcConfig = args.cdc;

    const fetchRows = async (uids: string[]): Promise<Record<string, unknown>[]> => {
      const inList = uids.map((uid) => `'${uid}'`).join(",");
      const where = [`uid IN (${inList})`];
      if (args.fetch) {
        // A function source cannot be narrowed by uid from here; take its full
        // answer and keep the rows asked for.
        const want = new Set(uids);
        return (await args.fetch([])).map(remapRowKeys).filter((r) => want.has(String(r["uid"])));
      }
      return runSql(appendSqlWhere(baseSql(), where));
    };

    const refetchRows = async (uids: string[]) => {
      // Re-checked at flush time: the parent may have been deselected inside the window.
      if (!gateOpen()) return;
      const resolvedRows = await fetchRows(uids);
      // Row identity here is the CDC row's uid, which is what fetchRows filters on; a
      // multi-key view (rowKey) can return several rows for one uid, all upserted.
      const foundUids = new Set(resolvedRows.map((r) => String(r["uid"])));
      resolvedRows.forEach((r) => subject.next({ type: "upsert", row: r }));
      uids.forEach((rowId) => {
        if (!foundUids.has(rowId)) subject.next({ type: "delete", rowId });
      });
    };
    cdcCoalescer = createCdcRefetchCoalescer(refetchRows);

    const cdcListener = (
      changeEvent: ChangeDataCaptureEvent,
      row: Record<string, unknown>,
    ) => {
      // Ungated child view: ignore CDC — a row change on the underlying table
      // must not populate a panel whose parent isn't selected.
      if (!gateOpen()) return;
      if (changeEvent.table === cdcConfig.table) {
        cdcCoalescer?.add([row["uid"] as string]);
      }
    };

    GlobalClient.get().cdcSubscribe<Record<string, unknown>>(
      {
        tables: [{ database: cdcConfig.database, table: cdcConfig.table }],
        startSeq: "new",
      },
      cdcListener,
    );
  }

  if (args.refreshInterval) {
    const refreshInterval = args.refreshInterval;
    // Reuse the shared refreshState created above (don't shadow it).

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let lastCdcUpdateTime = 0;
    const cdcUpdateThrottle = Duration.fromSeconds(2);

    const performRefresh = async () => {
      const now = Date.now();
      if (now - lastCdcUpdateTime < cdcUpdateThrottle.toMilliseconds()) {
        scheduleNextRefresh();
        return;
      }
      // fetchAll publishes the refresh state, including a failure.
      try {
        await fetchAll();
      } catch (error) {
        console.error("Error during periodic refresh:", error);
      }
      scheduleNextRefresh();
    };

    const scheduleNextRefresh = () => {
      timeoutId = setTimeout(performRefresh, refreshInterval.toMilliseconds());
    };

    if (args.cdc) {
      const originalNext = subject.next.bind(subject);
      subject.next = (value: RowChange<Record<string, unknown>>) => {
        lastCdcUpdateTime = Date.now();
        originalNext(value);
      };
    }

    fetchAll().then(() => scheduleNextRefresh());

    tableModel.cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      refreshState.complete();
    };
  } else {
    fetchAll();
  }

  if (cdcCoalescer) {
    const coalescer = cdcCoalescer;
    const previousCleanup = tableModel.cleanup;
    tableModel.cleanup = () => {
      coalescer.cancel();
      previousCleanup?.();
    };
  }

  return tableModel;
}

interface Filter {
  readonly label: string;
  readonly filterType: FilterType;
  value: unknown;
  isSatisfied(): boolean;
}

export interface BiqlFilter extends Filter {
  readonly fieldName: string;
  readonly dependency?: ViewDependency;
  toWhereClause(): string;
}

export class BiqlFilterImpl implements BiqlFilter {
  private readonly _fieldName: string;
  private readonly _label: string;
  private readonly _filterType: FilterType;
  private readonly _dependency?: ViewDependency;
  private _value: unknown;

  constructor({
    fieldName,
    filterType,
    label,
    dependency,
  }: {
    fieldName: string;
    filterType: FilterType;
    label: string;
    dependency?: ViewDependency;
  }) {
    this._fieldName = fieldName;
    this._label = label;
    this._dependency = dependency;
    this._filterType = filterType;
  }

  get value(): unknown {
    return this._value;
  }
  set value(v: unknown) {
    this._value = v;
  }

  get fieldName(): string {
    return this._fieldName;
  }

  get label(): string {
    return this._label;
  }

  get dependency(): ViewDependency | undefined {
    return this._dependency;
  }

  get filterType(): FilterType {
    return this._filterType;
  }

  isSatisfied(): boolean {
    // true is `value` is defined and not null
    if (this.value !== undefined && this.value !== null) {
      return true;
    }
    return false;
  }

  toWhereClause(): string {
    const whereifierFcn = (val: unknown): string => {
      switch (this.filterType) {
        case "STRING":
          // SQL string literals use SINGLE quotes. Double quotes denote an
          // IDENTIFIER (column name), so `schedule_uid = "abc"` was parsed as a
          // column comparison and failed with "column not found", causing the
          // schedule->events filter to silently fall back to showing everything.
          return `'${String(val).replace(/'/g, "''")}'`;
        case "NUMBER":
          return `${val}`;
        case "DATE":
          return `DATE('${String(val).replace(/'/g, "''")}')`;
        default:
          return JSON.stringify(val);
      }
    };

    // if value is an array, use IN clause
    if (Array.isArray(this.value)) {
      const valuesList = this.value.map((v) => whereifierFcn(v)).join(", ");
      return `${this.fieldName} IN (${valuesList})`;
    } else {
      return `${this.fieldName} = ${whereifierFcn(this.value)}`;
    }
  }
}

export type FilterType = "STRING" | "NUMBER" | "DATE";
